
import * as net from "@ivanlog/net";
import {NBResult} from "./models";
import {DEXPriceCandle} from "./models";
import {DEXTokenTierView} from "./models";
import {DEXTrackedPoolReserveView} from "./models";
import {DEXSwapExecutionPlanView} from "./models";
import {DEXSwapExecutionRouteView} from "./models";
import {DEXSwapRouteCandidateView} from "./models";

export class DEXWatcher {
	private readonly _client: net.HTTPClient;
	private readonly _errorsHandler: (e: unknown, args?: unknown) => void;
	/**
	* @param {net.HTTPClient} _client - 
	* @param {(e: unknown, args?: unknown) => void} _errorsHandler - 
	*/
	constructor (_client: net.HTTPClient, _errorsHandler: (e: unknown, args?: unknown) => void){
		this._client = _client;
		this._errorsHandler = _errorsHandler;
		if (!this._errorsHandler) this._errorsHandler = (e, args) => { throw e; };
	}
	
	/**
	* @param {Array<string>} erc20Adrs - 
	* @returns {Promise<NBResult|Array<number>|net.NetError|number>}
	*/
	public async queryMidPricesUnsafe(erc20Adrs: Array<string>): Promise<NBResult|Array<number>|net.NetError|number> {
		const __endpoint="/DEXWatcher/QueryMidPrices";
		const __query:string[]=[];
		const __addHeaders: Record<string,string> = {};
		__addHeaders['Accept'] = 'application/json';
		__addHeaders['Content-Type'] = 'application/json';
		let __url = __endpoint;
		let __data;
		const __bodyDataObj = {} as any;
		__bodyDataObj.erc20Adrs = erc20Adrs;
		__data = net.def.objToClearJSON(__bodyDataObj);
		const __response = await this._client.process(net.HTTPMethods.POST, __url, __data, __addHeaders, [200]);
		const __code: number = __response.status;
		let __result;
		try {
			switch(__code) {
				case 200: const __resultArr = await __response.json(); __result=[]; for (const __r of __resultArr) __result.push(__r); break;
				case 401: __result = new NBResult(await __response.json()); break;
				case 403: __result = new NBResult(await __response.json()); break;
				case 422: __result = new NBResult(await __response.json()); break;
				case 500: __result = new NBResult(await __response.json()); break;
				default: throw new net.NetError(net.HTTPCodes.UnknownResponse);
			}
		} catch(e) { throw new net.NetError(net.HTTPCodes.BadDataFormat); }
		if ((__code>=200)&&(__code<300))
		{ if (__result) return __result; else return __code; } else
		{ if (__result) throw __result; else throw new net.NetError(__code); }
	}
	
	/**
	* @param {Array<string>} erc20Adrs - 
	* @param {unknown} errHndlArgs? - args for errorsHandler
	* @returns {Promise<Array<number>|undefined>}
	*/
	public async queryMidPrices(erc20Adrs: Array<string>, errHndlArgs?: unknown): Promise<Array<number>|undefined> {
		try {
			const r = await this.queryMidPricesUnsafe(erc20Adrs);
			if (!(Array.isArray(r))) throw new net.NetError(net.HTTPCodes.UnknownResponse); else return r;
		} catch (e) { this._errorsHandler(e, errHndlArgs); }
	}
	
	/**
	* @param {string} baseTokenAddress - 
	* @param {string} quoteTokenAddress - 
	* @param {string} interval - 
	* @param {number} count - 
	* @returns {Promise<NBResult|Array<DEXPriceCandle>|net.NetError|number>}
	*/
	public async getCandlesUnsafe(baseTokenAddress: string, quoteTokenAddress: string, interval: string, count: number): Promise<NBResult|Array<DEXPriceCandle>|net.NetError|number> {
		const __endpoint="/DEXWatcher/GetCandles";
		const __query:string[]=[];
		const __addHeaders: Record<string,string> = {};
		__addHeaders['Accept'] = 'application/json';
		let __url = __endpoint;
		__query.push("baseTokenAddress="+encodeURIComponent(baseTokenAddress));
		__query.push("quoteTokenAddress="+encodeURIComponent(quoteTokenAddress));
		__query.push("interval="+encodeURIComponent(interval));
		__query.push("count="+encodeURIComponent(count));
		__url=__url+'?'+__query.join('&');
		const __response = await this._client.process(net.HTTPMethods.GET, __url, undefined, __addHeaders, [200]);
		const __code: number = __response.status;
		let __result;
		try {
			switch(__code) {
				case 200: const __resultArr = await __response.json(); __result=[]; for (const __r of __resultArr) __result.push(new DEXPriceCandle(__r)); break;
				case 401: __result = new NBResult(await __response.json()); break;
				case 403: __result = new NBResult(await __response.json()); break;
				case 422: __result = new NBResult(await __response.json()); break;
				case 500: __result = new NBResult(await __response.json()); break;
				default: throw new net.NetError(net.HTTPCodes.UnknownResponse);
			}
		} catch(e) { throw new net.NetError(net.HTTPCodes.BadDataFormat); }
		if ((__code>=200)&&(__code<300))
		{ if (__result) return __result; else return __code; } else
		{ if (__result) throw __result; else throw new net.NetError(__code); }
	}
	
	/**
	* @param {string} baseTokenAddress - 
	* @param {string} quoteTokenAddress - 
	* @param {string} interval - 
	* @param {number} count - 
	* @param {unknown} errHndlArgs? - args for errorsHandler
	* @returns {Promise<Array<DEXPriceCandle>|undefined>}
	*/
	public async getCandles(baseTokenAddress: string, quoteTokenAddress: string, interval: string, count: number, errHndlArgs?: unknown): Promise<Array<DEXPriceCandle>|undefined> {
		try {
			const r = await this.getCandlesUnsafe(baseTokenAddress, quoteTokenAddress, interval, count);
			if (!(Array.isArray(r))) throw new net.NetError(net.HTTPCodes.UnknownResponse); else return r;
		} catch (e) { this._errorsHandler(e, errHndlArgs); }
	}
	
	/**
	* @param {number} skip - 
	* @param {number} take - 
	* @param {string} filter - 
	* @returns {Promise<NBResult|Array<DEXTokenTierView>|net.NetError|number>}
	*/
	public async getTokensByTierUnsafe(skip: number, take: number, filter: string): Promise<NBResult|Array<DEXTokenTierView>|net.NetError|number> {
		const __endpoint="/DEXWatcher/GetTokensByTier";
		const __query:string[]=[];
		const __addHeaders: Record<string,string> = {};
		__addHeaders['Accept'] = 'application/json';
		let __url = __endpoint;
		__query.push("skip="+encodeURIComponent(skip));
		__query.push("take="+encodeURIComponent(take));
		__query.push("filter="+encodeURIComponent(filter));
		__url=__url+'?'+__query.join('&');
		const __response = await this._client.process(net.HTTPMethods.GET, __url, undefined, __addHeaders, [200]);
		const __code: number = __response.status;
		let __result;
		try {
			switch(__code) {
				case 200: const __resultArr = await __response.json(); __result=[]; for (const __r of __resultArr) __result.push(new DEXTokenTierView(__r)); break;
				case 401: __result = new NBResult(await __response.json()); break;
				case 403: __result = new NBResult(await __response.json()); break;
				case 422: __result = new NBResult(await __response.json()); break;
				case 500: __result = new NBResult(await __response.json()); break;
				default: throw new net.NetError(net.HTTPCodes.UnknownResponse);
			}
		} catch(e) { throw new net.NetError(net.HTTPCodes.BadDataFormat); }
		if ((__code>=200)&&(__code<300))
		{ if (__result) return __result; else return __code; } else
		{ if (__result) throw __result; else throw new net.NetError(__code); }
	}
	
	/**
	* @param {number} skip - 
	* @param {number} take - 
	* @param {string} filter - 
	* @param {unknown} errHndlArgs? - args for errorsHandler
	* @returns {Promise<Array<DEXTokenTierView>|undefined>}
	*/
	public async getTokensByTier(skip: number, take: number, filter: string, errHndlArgs?: unknown): Promise<Array<DEXTokenTierView>|undefined> {
		try {
			const r = await this.getTokensByTierUnsafe(skip, take, filter);
			if (!(Array.isArray(r))) throw new net.NetError(net.HTTPCodes.UnknownResponse); else return r;
		} catch (e) { this._errorsHandler(e, errHndlArgs); }
	}
	
	/**
	* @param {Array<string>} poolAddresses - 
	* @returns {Promise<NBResult|Array<DEXTrackedPoolReserveView>|net.NetError|number>}
	*/
	public async queryTrackedPoolReservesUnsafe(poolAddresses: Array<string>): Promise<NBResult|Array<DEXTrackedPoolReserveView>|net.NetError|number> {
		const __endpoint="/DEXWatcher/QueryTrackedPoolReserves";
		const __query:string[]=[];
		const __addHeaders: Record<string,string> = {};
		__addHeaders['Accept'] = 'application/json';
		__addHeaders['Content-Type'] = 'application/json';
		let __url = __endpoint;
		let __data;
		const __bodyDataObj = {} as any;
		__bodyDataObj.poolAddresses = poolAddresses;
		__data = net.def.objToClearJSON(__bodyDataObj);
		const __response = await this._client.process(net.HTTPMethods.POST, __url, __data, __addHeaders, [200]);
		const __code: number = __response.status;
		let __result;
		try {
			switch(__code) {
				case 200: const __resultArr = await __response.json(); __result=[]; for (const __r of __resultArr) __result.push(new DEXTrackedPoolReserveView(__r)); break;
				case 401: __result = new NBResult(await __response.json()); break;
				case 403: __result = new NBResult(await __response.json()); break;
				case 422: __result = new NBResult(await __response.json()); break;
				case 500: __result = new NBResult(await __response.json()); break;
				default: throw new net.NetError(net.HTTPCodes.UnknownResponse);
			}
		} catch(e) { throw new net.NetError(net.HTTPCodes.BadDataFormat); }
		if ((__code>=200)&&(__code<300))
		{ if (__result) return __result; else return __code; } else
		{ if (__result) throw __result; else throw new net.NetError(__code); }
	}
	
	/**
	* @param {Array<string>} poolAddresses - 
	* @param {unknown} errHndlArgs? - args for errorsHandler
	* @returns {Promise<Array<DEXTrackedPoolReserveView>|undefined>}
	*/
	public async queryTrackedPoolReserves(poolAddresses: Array<string>, errHndlArgs?: unknown): Promise<Array<DEXTrackedPoolReserveView>|undefined> {
		try {
			const r = await this.queryTrackedPoolReservesUnsafe(poolAddresses);
			if (!(Array.isArray(r))) throw new net.NetError(net.HTTPCodes.UnknownResponse); else return r;
		} catch (e) { this._errorsHandler(e, errHndlArgs); }
	}
	
	/**
	* @param {string} tokenInAddress - 
	* @param {string} tokenOutAddress - 
	* @param {number} amountIn - 
	* @param {string} key - 
	* @returns {Promise<NBResult|DEXSwapExecutionPlanView|net.NetError|number>}
	*/
	public async getOptimalSwapExecutionPlanUnsafe(tokenInAddress: string, tokenOutAddress: string, amountIn: number, key: string): Promise<NBResult|DEXSwapExecutionPlanView|net.NetError|number> {
		const __endpoint="/DEXWatcher/GetOptimalSwapExecutionPlan";
		const __query:string[]=[];
		const __addHeaders: Record<string,string> = {};
		__addHeaders['Accept'] = 'application/json';
		let __url = __endpoint;
		__query.push("tokenInAddress="+encodeURIComponent(tokenInAddress));
		__query.push("tokenOutAddress="+encodeURIComponent(tokenOutAddress));
		__query.push("amountIn="+encodeURIComponent(amountIn));
		__query.push("key="+encodeURIComponent(key));
		__url=__url+'?'+__query.join('&');
		const __response = await this._client.process(net.HTTPMethods.GET, __url, undefined, __addHeaders, [200]);
		const __code: number = __response.status;
		let __result;
		try {
			switch(__code) {
				case 200: __result = new DEXSwapExecutionPlanView(await __response.json()); break;
				case 401: __result = new NBResult(await __response.json()); break;
				case 403: __result = new NBResult(await __response.json()); break;
				case 422: __result = new NBResult(await __response.json()); break;
				case 500: __result = new NBResult(await __response.json()); break;
				default: throw new net.NetError(net.HTTPCodes.UnknownResponse);
			}
		} catch(e) { throw new net.NetError(net.HTTPCodes.BadDataFormat); }
		if ((__code>=200)&&(__code<300))
		{ if (__result) return __result; else return __code; } else
		{ if (__result) throw __result; else throw new net.NetError(__code); }
	}
	
	/**
	* @param {string} tokenInAddress - 
	* @param {string} tokenOutAddress - 
	* @param {number} amountIn - 
	* @param {string} key - 
	* @param {unknown} errHndlArgs? - args for errorsHandler
	* @returns {Promise<DEXSwapExecutionPlanView|undefined>}
	*/
	public async getOptimalSwapExecutionPlan(tokenInAddress: string, tokenOutAddress: string, amountIn: number, key: string, errHndlArgs?: unknown): Promise<DEXSwapExecutionPlanView|undefined> {
		try {
			const r = await this.getOptimalSwapExecutionPlanUnsafe(tokenInAddress, tokenOutAddress, amountIn, key);
			if (!(r instanceof DEXSwapExecutionPlanView)) throw new net.NetError(net.HTTPCodes.UnknownResponse); else return r;
		} catch (e) { this._errorsHandler(e, errHndlArgs); }
	}
	
	/**
	* @param {string} tokenInAddress - 
	* @param {string} tokenOutAddress - 
	* @param {number} amountIn - 
	* @param {string} key - 
	* @returns {Promise<NBResult|DEXSwapExecutionRouteView|net.NetError|number>}
	*/
	public async getOptimalSwapRouteWithReservesUnsafe(tokenInAddress: string, tokenOutAddress: string, amountIn: number, key: string): Promise<NBResult|DEXSwapExecutionRouteView|net.NetError|number> {
		const __endpoint="/DEXWatcher/GetOptimalSwapRouteWithReserves";
		const __query:string[]=[];
		const __addHeaders: Record<string,string> = {};
		__addHeaders['Accept'] = 'application/json';
		let __url = __endpoint;
		__query.push("tokenInAddress="+encodeURIComponent(tokenInAddress));
		__query.push("tokenOutAddress="+encodeURIComponent(tokenOutAddress));
		__query.push("amountIn="+encodeURIComponent(amountIn));
		__query.push("key="+encodeURIComponent(key));
		__url=__url+'?'+__query.join('&');
		const __response = await this._client.process(net.HTTPMethods.GET, __url, undefined, __addHeaders, [200]);
		const __code: number = __response.status;
		let __result;
		try {
			switch(__code) {
				case 200: __result = new DEXSwapExecutionRouteView(await __response.json()); break;
				case 401: __result = new NBResult(await __response.json()); break;
				case 403: __result = new NBResult(await __response.json()); break;
				case 422: __result = new NBResult(await __response.json()); break;
				case 500: __result = new NBResult(await __response.json()); break;
				default: throw new net.NetError(net.HTTPCodes.UnknownResponse);
			}
		} catch(e) { throw new net.NetError(net.HTTPCodes.BadDataFormat); }
		if ((__code>=200)&&(__code<300))
		{ if (__result) return __result; else return __code; } else
		{ if (__result) throw __result; else throw new net.NetError(__code); }
	}
	
	/**
	* @param {string} tokenInAddress - 
	* @param {string} tokenOutAddress - 
	* @param {number} amountIn - 
	* @param {string} key - 
	* @param {unknown} errHndlArgs? - args for errorsHandler
	* @returns {Promise<DEXSwapExecutionRouteView|undefined>}
	*/
	public async getOptimalSwapRouteWithReserves(tokenInAddress: string, tokenOutAddress: string, amountIn: number, key: string, errHndlArgs?: unknown): Promise<DEXSwapExecutionRouteView|undefined> {
		try {
			const r = await this.getOptimalSwapRouteWithReservesUnsafe(tokenInAddress, tokenOutAddress, amountIn, key);
			if (!(r instanceof DEXSwapExecutionRouteView)) throw new net.NetError(net.HTTPCodes.UnknownResponse); else return r;
		} catch (e) { this._errorsHandler(e, errHndlArgs); }
	}
	
	/**
	* @param {string} tokenInAddress - 
	* @param {string} tokenOutAddress - 
	* @param {number} amountIn - 
	* @param {string} key - 
	* @returns {Promise<NBResult|Array<DEXSwapRouteCandidateView>|net.NetError|number>}
	*/
	public async getSwapRouteCandidatesUnsafe(tokenInAddress: string, tokenOutAddress: string, amountIn: number, key: string): Promise<NBResult|Array<DEXSwapRouteCandidateView>|net.NetError|number> {
		const __endpoint="/DEXWatcher/GetSwapRouteCandidates";
		const __query:string[]=[];
		const __addHeaders: Record<string,string> = {};
		__addHeaders['Accept'] = 'application/json';
		let __url = __endpoint;
		__query.push("tokenInAddress="+encodeURIComponent(tokenInAddress));
		__query.push("tokenOutAddress="+encodeURIComponent(tokenOutAddress));
		__query.push("amountIn="+encodeURIComponent(amountIn));
		__query.push("key="+encodeURIComponent(key));
		__url=__url+'?'+__query.join('&');
		const __response = await this._client.process(net.HTTPMethods.GET, __url, undefined, __addHeaders, [200]);
		const __code: number = __response.status;
		let __result;
		try {
			switch(__code) {
				case 200: const __resultArr = await __response.json(); __result=[]; for (const __r of __resultArr) __result.push(new DEXSwapRouteCandidateView(__r)); break;
				case 401: __result = new NBResult(await __response.json()); break;
				case 403: __result = new NBResult(await __response.json()); break;
				case 422: __result = new NBResult(await __response.json()); break;
				case 500: __result = new NBResult(await __response.json()); break;
				default: throw new net.NetError(net.HTTPCodes.UnknownResponse);
			}
		} catch(e) { throw new net.NetError(net.HTTPCodes.BadDataFormat); }
		if ((__code>=200)&&(__code<300))
		{ if (__result) return __result; else return __code; } else
		{ if (__result) throw __result; else throw new net.NetError(__code); }
	}
	
	/**
	* @param {string} tokenInAddress - 
	* @param {string} tokenOutAddress - 
	* @param {number} amountIn - 
	* @param {string} key - 
	* @param {unknown} errHndlArgs? - args for errorsHandler
	* @returns {Promise<Array<DEXSwapRouteCandidateView>|undefined>}
	*/
	public async getSwapRouteCandidates(tokenInAddress: string, tokenOutAddress: string, amountIn: number, key: string, errHndlArgs?: unknown): Promise<Array<DEXSwapRouteCandidateView>|undefined> {
		try {
			const r = await this.getSwapRouteCandidatesUnsafe(tokenInAddress, tokenOutAddress, amountIn, key);
			if (!(Array.isArray(r))) throw new net.NetError(net.HTTPCodes.UnknownResponse); else return r;
		} catch (e) { this._errorsHandler(e, errHndlArgs); }
	}
}
